import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from "@/client/components/shad/sidebar";

import { mainNavItems } from "@/client/features/navigation/navItems";
import { ModeToggle } from "@/client/features/theme/ModeToggle";
import { useAuth } from "@/client/hooks/useAuth";
import { MapView } from "@/client/map/MapView";
import { useAuthenticate } from "@/client/providers/AuthProvider";
import { IconLock, IconLockAccess } from "@tabler/icons-react";

import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { MapIcon, SettingsIcon } from "lucide-react";
import type { CSSProperties } from "react";

export const Route = createFileRoute("/(protected)/map")({
  component: RouteComponent,
});

function RouteComponent() {
  const { logout } = useAuth();

  return (
    <SidebarProvider
      style={
        {
          "--sidebar-width": "320px",
        } as CSSProperties
      }
    >
      <Sidebar
        collapsible="icon"
        className="overflow-hidden *:data-[sidebar=sidebar]:flex-row"
      >
        <Sidebar
          collapsible="none"
          className="w-[calc(var(--sidebar-width-icon)+1px)]! border-r"
        >
          <SidebarHeader>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  className="px-2.5 md:px-2 hover:bg-lime-500"
                >
                  <Link
                    className="px-2.5 md:px-2 text-white duration-300  bg-lime-600  hover:bg-lime-500 hover:text-white"
                    to="/map"
                  >
                    <MapIcon />
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarHeader>
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupContent className="px-1.5 md:px-0">
                <SidebarMenu>
                  {mainNavItems.map((item) => (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        asChild
                        tooltip={{
                          children: item.label,
                          hidden: false,
                        }}
                        className="px-2.5 md:px-2"
                      >
                        <Link to={item.to}>
                          {item.icon ? <item.icon /> : null}
                          <span>{item.label}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
            <div className="-my-1 border-b border-border/20" />
            <SidebarGroup>
              <SidebarGroupContent className="px-1.5 md:px-0">
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      tooltip={{
                        children: "Settings",
                        hidden: false,
                      }}
                      className="px-2.5 md:px-2"
                    >
                      <Link to="/settings/map" reloadDocument>
                        <SettingsIcon />
                        <span>Settings</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
          <SidebarFooter>
            <SidebarMenu>
              <SidebarMenuItem>
                <ModeToggle />
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={logout}
                  tooltip={{
                    children: "Lock",
                    hidden: false,
                  }}
                  className="px-2.5 md:px-2"
                >
                  <IconLock />
                  <span>Lock</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarFooter>
        </Sidebar>

        <Sidebar collapsible="none">
          <Outlet />
        </Sidebar>
      </Sidebar>

      <SidebarInset>
        <div className="relative h-screen w-full overflow-clip">
          <div className="absolute h-screen w-full">
            <MapView />
          </div>
        </div>
        <SidebarTrigger className="absolute top-2.5 left-6 bg-background size-9 shadow-lg rounded-sm hover:bg-background" />
      </SidebarInset>
    </SidebarProvider>
  );
}
